import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AuthFlowType,
  B2BProducts,
  StytchB2B,
  useStytchB2BClient,
} from '@stytch/react/b2b';
import { useLocation } from 'react-router-dom';
import { authErrorMessage, authErrorReason, UserFacingAuthError } from '../auth/authErrorMessage';
import { desktopFeedbackRequestUrl } from '../auth/desktopHandoff';
import {
  isInvitationCallback,
  readAuthenticateSessionState,
  resolveSessionOutcome,
  type OrgResolutionSources,
} from '../auth/invitationHandoff';
import { invitationLandingFailed, invitationLandingViewed } from '../auth/invitationHandoffEvents';
import { createPersonalOrg, shouldRetryPersonalTag, tagPersonalOrg } from '../auth/personalOrg';
import {
  authenticatedLandingPath,
  consumeReturnPath,
  peekReturnPath,
  rememberReturnPath,
} from '../auth/returnPath';
import { ThemePicker, Wordmark } from '../components/Chrome';
import {
  ALERT_ERROR,
  CALLBACK_CARD,
  CALLBACK_SCREEN,
  EYEBROW,
} from '../controlStyles';
import { DecorativeIcon } from '../organization/DecorativeIcon';
import { consoleConfig, webAuthCallbackUrl } from '../config';
import { resolveConsoleRoute } from '../routing';
import { getThemeColors, getThemeColorScheme, useTheme, type ThemeId } from '../theme';

interface DiscoveredOrganization {
  organization: {
    organization_id: string;
    organization_name: string;
    trusted_metadata?: Record<string, unknown> | null;
  };
}

interface DiscoveryOrganizationsResponse {
  email_address?: string;
  discovered_organizations?: DiscoveredOrganization[];
}

/** Mirrors `--font-app` in styles.css, which mirrors the desktop renderer. */
const APP_FONT_STACK =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

const loginConfig = {
  authFlowType: AuthFlowType.Discovery,
  products: [B2BProducts.emailMagicLinks, B2BProducts.oauth],
  emailMagicLinksOptions: { discoveryRedirectURL: webAuthCallbackUrl },
  oauthOptions: {
    discoveryRedirectURL: webAuthCallbackUrl,
    providers: [{ type: 'google' as const }, { type: 'github' as const }],
  },
  sessionOptions: { sessionDurationMinutes: consoleConfig.sessionDurationMinutes },
};

function stytchPresentation(theme: ThemeId) {
  const colors = getThemeColors(theme);
  return {
    theme: {
      'color-scheme': getThemeColorScheme(theme),
      /* Stytch renders in an iframe-free shadow surface that does not inherit
         our `body`, so the app stack is restated here rather than read from
         `--font-app`; keep the two in step. */
      'font-family': APP_FONT_STACK,
      'header-font': APP_FONT_STACK,
      'container-width': '100%',
      'rounded-base': '4px',
      background: 'transparent',
      'container-border': 'transparent',
      foreground: colors.text,
      primary: colors.primary,
      'primary-foreground': colors['on-primary'],
      'primary-button-hover': colors['primary-hover'],
      secondary: colors['bg-secondary'],
      'secondary-foreground': colors.text,
      muted: colors['bg-tertiary'],
      'muted-foreground': colors['text-muted'],
      accent: colors['bg-hover'],
      'accent-foreground': colors.text,
      border: colors.border,
      input: colors.border,
      ring: colors['border-focus'],
      destructive: colors.error,
      success: colors.success,
    },
    options: { hideHeaderText: true },
  } as const;
}

/**
 * Another way in on the same screen: the GitHub-native wiki routes (behind
 * `VITE_WIKI_GITHUB_NATIVE=on`, loaded lazily) pass one so this module never
 * imports the GitHub session.
 */
export interface LoginVariant {
  id: string;
  eyebrow: string;
  subtitle: string;
  intro: ReactNode;
  signIn: ReactNode;
}

/** The sign-in screen: Stytch discovery, or a `variant`'s own sign-in. */
export function LoginScreen({ variant }: { variant?: LoginVariant }) {
  const { theme } = useTheme();
  const location = useLocation();
  const presentation = useMemo(() => stytchPresentation(theme), [theme]);
  const protectedLink = location.pathname.startsWith('/org/');
  const route = useMemo(() => resolveConsoleRoute(location.pathname), [location.pathname]);
  const coldFeedbackLink = route.kind === 'feedbackRequest' ? route : null;

  useEffect(() => {
    rememberReturnPath(`${location.pathname}${location.search}${location.hash}`);
  }, [location.hash, location.pathname, location.search]);

  return (
    // The two-column split collapses on a narrow page; that override is a real
    // `@container` at-rule in `styles.css`.
    <main
      className="login-screen grid min-h-screen bg-[radial-gradient(circle_at_12%_18%,var(--nim-bg-selected),transparent_32rem),radial-gradient(circle_at_78%_88%,var(--nim-bg-selected),transparent_30rem),var(--nim-bg)]"
      data-cold-link={coldFeedbackLink ? 'feedbackRequest' : undefined}
      data-provider={variant?.id ?? 'stytch'}
    >
      <section className="login-intro relative flex min-h-screen flex-col items-start justify-center border-r border-nim px-[clamp(2.5rem,7vw,7rem)] py-20">
        <div className="login-topbar absolute left-[clamp(2.5rem,7vw,7rem)] right-[clamp(2.5rem,7vw,7rem)] top-[2.2rem] flex items-center justify-between gap-4">
          <Wordmark /><ThemePicker />
        </div>
        {variant ? variant.intro : coldFeedbackLink ? <FeedbackColdLinkIntro route={coldFeedbackLink} /> : (
          <>
            <div className={`${EYEBROW} mb-[1.1rem]`}>Team workspace</div>
            <h1 className={LOGIN_HEADLINE}>Open your organization&rsquo;s shared work.</h1>
            <p className={LOGIN_LEDE}>
              Sign in with the account connected to your organization. This console
              uses the same collaboration service and permissions as the desktop app.
            </p>
            <div className="security-note flex items-center gap-[0.6rem] text-[0.86rem] text-nim-muted">
              {/* Tinted from the canonical semantic colour rather than a second
                  palette, so the halo tracks whatever the theme sets. */}
              <span
                className="size-2 rounded-full bg-[var(--nim-success)] shadow-[0_0_0_4px_color-mix(in_srgb,var(--nim-success)_12%,transparent)]"
                aria-hidden="true"
              />
              Browser sessions are scoped to one signed-in account.
            </div>
          </>
        )}
      </section>
      <section
        className="login-card w-[min(31rem,calc(100%-4rem))] self-center justify-self-center rounded-2xl border border-nim bg-nim p-10 shadow-[var(--console-shadow-lg)] [&>div:last-child]:w-full"
        aria-label="Sign in"
      >
        <div className="login-card-heading mb-[1.6rem]">
          <span className={EYEBROW}>{variant ? variant.eyebrow : coldFeedbackLink ? 'Feedback request' : 'Workspace access'}</span>
          <h2 className="mb-[0.4rem] mt-[0.6rem] text-[1.7rem] font-semibold tracking-[-0.02em]">Sign in to continue</h2>
          <p className="text-[0.93rem] leading-normal text-nim-muted">
            {variant
              ? variant.subtitle
              : coldFeedbackLink
              ? 'Use the address the request was sent to, or a connected identity provider.'
              : 'Use a work email or a connected identity provider.'}
          </p>
        </div>
        {protectedLink && !coldFeedbackLink && (
          <div className={`login-session-alert mb-4 ${ALERT_ERROR}`} role="status">
            Your session is not active. Sign in to continue to this organization link.
          </div>
        )}
        {variant
          ? variant.signIn
          : <StytchB2B config={loginConfig} presentation={presentation} />}
      </section>
    </main>
  );
}

export const LOGIN_HEADLINE = 'max-w-[22ch] text-[clamp(2.6rem,4.6vw,4.2rem)] font-semibold leading-[1.05] tracking-[-0.03em]';
export const LOGIN_LEDE = 'my-[1.6rem] mb-9 max-w-[46ch] text-[1.02rem] leading-[1.7] text-nim-muted';

/**
 * The signed-out half of a pasted feedback link.
 *
 * This page cannot show the question and must not imply that it could. The
 * server hands out no part of a request without a team JWT, so the org name,
 * who asked, and what they asked are all unavailable here — anything on screen
 * naming them would be invented. So it names only what the URL already implies:
 * an organization exists, and a request in it is addressed to whoever holds the
 * link. Saying that plainly is a better first impression than a spinner, and it
 * is the only version of this page that is true.
 */
function FeedbackColdLinkIntro({
  route,
}: {
  route: { orgSlug: string; requestId: string };
}) {
  return (
    <>
      <div className={`${EYEBROW} mb-[1.1rem]`}>Someone asked for your feedback</div>
      <h1 className={LOGIN_HEADLINE}>Sign in to read the questions.</h1>
      <p className={LOGIN_LEDE}>
        Somebody in a Nimbalyst organization sent you this link to collect your
        answers. If you were invited by email, use that address.
      </p>
      <div className="cold-link-sealed mb-[1.6rem] grid w-[min(46ch,100%)] grid-cols-[auto_minmax(0,1fr)] items-start gap-[0.7rem] rounded-xl border border-nim bg-nim-secondary px-4 py-[0.85rem] text-[0.86rem] leading-relaxed text-nim-muted">
        <DecorativeIcon icon="lock" size={18} className="text-nim-faint" />
        <span>
          The request stays sealed until you sign in — including who asked and
          what they asked. Nobody outside the organization can read it from this
          link.
        </span>
      </div>
      {/* The route key is the organization identifier the desktop deep link
          takes; there is no canonical org slug yet (see `getOrgRouteKey`). */}
      <p className="cold-link-handoff m-0 max-w-[46ch] text-[0.86rem] leading-relaxed text-nim-muted">
        <a
          className="font-medium text-[var(--nim-primary)] no-underline hover:underline"
          href={desktopFeedbackRequestUrl(route.orgSlug, route.requestId)}
        >
          Open this request in Nimbalyst
        </a>
        {' '}instead. You do not need the app to answer — the browser is enough.
      </p>
    </>
  );
}

type B2BClient = ReturnType<typeof useStytchB2BClient>;

function discoveryIdentities(response: DiscoveryOrganizationsResponse) {
  return (response.discovered_organizations || []).map(({ organization }) => ({
    orgId: organization.organization_id,
    orgName: organization.organization_name,
  }));
}

/**
 * An already-signed-in invitee gets an authenticate response with no
 * organization on it, so the session the invitation just scoped is the next
 * place to look, and discovery the one after that.
 */
function orgResolutionSources(stytch: B2BClient): OrgResolutionSources {
  return {
    sessionOrganization: () => {
      const organization = stytch.organization.getSync();
      return organization
        ? { orgId: organization.organization_id, orgName: organization.organization_name }
        : null;
    },
    sessionOrganizationId: () => stytch.session.getSync()?.organization_id ?? null,
    listDiscoveredOrganizations: async () => discoveryIdentities(
      await stytch.discovery.organizations.list() as DiscoveryOrganizationsResponse,
    ),
  };
}

/**
 * Whether this browser holds a session, asked properly rather than guessed.
 *
 * `getTokens()` is a cache read and comes back empty for reasons that have
 * nothing to do with membership, so a miss is followed by the same refresh
 * `getFreshBrowserSessionJwt` does. Only used on the failure path, where one
 * extra round trip is cheaper than telling an accepted invitee they are not in.
 */
async function hasLiveSession(stytch: B2BClient): Promise<boolean> {
  if (stytch.session.getSync()) return true;
  try {
    const refreshed = await stytch.session.authenticate({
      session_duration_minutes: consoleConfig.sessionDurationMinutes,
    });
    return typeof refreshed?.session_jwt === 'string' && refreshed.session_jwt.length > 0;
  } catch {
    return false;
  }
}

export function AuthenticationCallback() {
  const stytch = useStytchB2BClient();
  const started = useRef(false);
  const [organizations, setOrganizations] = useState<DiscoveredOrganization[]>([]);
  const isInvitation = isInvitationCallback(window.location.search);
  const relayedReturnPath = new URLSearchParams(window.location.search).get('returnPath');
  const [status, setStatus] = useState(isInvitation ? 'Accepting your organization invitation…' : 'Finishing secure sign-in…');
  const [error, setError] = useState<string | null>(null);
  const [exchangingOrgId, setExchangingOrgId] = useState<string | null>(null);

  const finishRedirect = useCallback((path?: string) => {
    if (path) consumeReturnPath();
    window.location.replace(path ?? authenticatedLandingPath(isInvitation));
  }, [isInvitation]);

  const exchangeOrganization = useCallback(async (organizationId: string, retryPersonalTag = false) => {
    setExchangingOrgId(organizationId);
    setError(null);
    setStatus('Opening your organization…');
    try {
      const result = await stytch.discovery.intermediateSessions.exchange({
        organization_id: organizationId,
        session_duration_minutes: consoleConfig.sessionDurationMinutes,
      });
      if (!result.session_jwt) throw new UserFacingAuthError('This organization requires an additional authentication step.');
      // A personal org whose tag failed at creation; the outcome is only logged.
      if (retryPersonalTag) await tagPersonalOrg(result.session_jwt);
      finishRedirect();
    } catch (cause) {
      console.error('[auth] organization exchange failed', cause);
      setExchangingOrgId(null);
      setStatus('Choose an organization');
      setError(authErrorMessage(cause, 'We could not open this organization. Please try again.'));
    }
  }, [finishRedirect, stytch]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (isInvitation) invitationLandingViewed();
    /** Land in the console off a session that exists, whatever produced it. */
    const landInConsole = async (result: unknown) => {
      // The invitation is accepted at this point, and the invitee goes
      // straight into the console — the org they just joined if it could be
      // resolved, their feedback request if that is what brought them here.
      const outcome = await resolveSessionOutcome({
        isInvitation,
        rememberedReturnPath: peekReturnPath(),
        relayedReturnPath,
        result,
        sources: orgResolutionSources(stytch),
      });
      finishRedirect(outcome.path);
    };
    const finishAuthentication = async () => {
      try {
        const result = await stytch.authenticateByUrl({
          session_duration_minutes: consoleConfig.sessionDurationMinutes,
        });
        window.history.replaceState({}, '', '/authenticate');
        if (!result) throw new UserFacingAuthError('The sign-in link is missing or has already been used.');
        // The response says which session it established. Only when it says
        // nothing recognizable does the SDK's token cache get a say.
        const sessionState = readAuthenticateSessionState(result);
        if (sessionState === 'member'
          || (sessionState === 'unknown' && stytch.session.getTokens()?.session_jwt)) {
          await landInConsole(result);
          return;
        }
        const discovery = await stytch.discovery.organizations.list() as DiscoveryOrganizationsResponse;
        const available = discovery.discovered_organizations || [];
        if (available.length === 0) {
          // A first sign-in: create the personal org desktop would have made.
          if (!discovery.email_address) throw new UserFacingAuthError('This account is not connected to an organization.');
          setStatus('Setting up your workspace…');
          const orgSessionJwt = await createPersonalOrg(stytch, discovery.email_address);
          if (!orgSessionJwt) throw new UserFacingAuthError('Your workspace requires an additional authentication step.');
          // Without the tag the org still works as personal; the next sign-in retries it.
          await tagPersonalOrg(orgSessionJwt);
          finishRedirect();
          return;
        }
        if (available.length === 1) {
          const only = available[0]!.organization;
          await exchangeOrganization(only.organization_id, shouldRetryPersonalTag(only, discovery.email_address));
          return;
        }
        setOrganizations(available);
        setStatus('Choose an organization');
      } catch (cause) {
        console.error('[auth] authentication callback failed', cause);
        window.history.replaceState({}, '', '/authenticate');
        // A spent token and a session error both land here, and neither of them
        // means the invitee is out: the second click on an email link fails
        // against a browser that is already signed in. If a session exists, the
        // invitation was accepted and the only honest screen is the console.
        let recovered = await hasLiveSession(stytch);
        if (recovered) {
          try {
            await landInConsole(null);
          } catch (recoveryCause) {
            console.error('[auth] could not land a recovered session', recoveryCause);
            recovered = false;
          }
        }
        if (isInvitation) invitationLandingFailed({ reason: authErrorReason(cause), recovered });
        if (recovered) return;
        setStatus(isInvitation ? 'Invitation could not be accepted' : 'Sign-in could not be completed');
        setError(authErrorMessage(
          cause,
          isInvitation
            ? 'We could not accept this invitation. Please ask your organization admin to send a new one.'
            : 'We could not complete sign-in. Please request a new link and try again.',
        ));
      }
    };
    void finishAuthentication();
  }, [exchangeOrganization, finishRedirect, isInvitation, relayedReturnPath, stytch]);

  return (
    <main className={CALLBACK_SCREEN}>
      <section className={CALLBACK_CARD}>
        <div className="status-orbit mx-auto mb-[1.9rem] grid size-16 animate-pulse place-items-center rounded-full border border-nim" aria-hidden="true">
          <span className="size-4 rounded-full bg-[var(--nim-purple)]" />
        </div>
        <div className={EYEBROW}>Secure handoff</div>
        <h1 className={CALLBACK_HEADING}>{status}</h1>
        {error && <p className="text-[var(--nim-error)] leading-normal" role="alert">{error}</p>}
        {organizations.length > 0 && (
          <div className="organization-picker mt-[1.9rem] grid gap-[0.6rem]">
            {organizations.map(({ organization }) => (
              <button
                className="organization-option grid w-full cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-[0.9rem] rounded-xl border border-nim bg-nim px-4 py-[0.85rem] text-left text-nim transition-colors duration-150 enabled:hover:border-nim-focus enabled:hover:bg-nim-secondary disabled:cursor-not-allowed disabled:opacity-50"
                key={organization.organization_id}
                type="button"
                disabled={exchangingOrgId !== null}
                onClick={() => void exchangeOrganization(organization.organization_id)}
              >
                <span className={ORG_AVATAR}>{organization.organization_name.slice(0, 1).toUpperCase()}</span>
                <span className="min-w-0 truncate">{organization.organization_name}</span>
                <span className="text-[0.75rem] font-medium text-[var(--nim-primary)]">Open</span>
              </button>
            ))}
          </div>
        )}
        {error && <a className={TEXT_LINK} href="/">Return to sign in</a>}
      </section>
    </main>
  );
}

export const CALLBACK_HEADING = 'mb-4 mt-[0.9rem] text-[2rem] font-semibold tracking-[-0.025em]';
const ORG_AVATAR =
  'grid size-[2.4rem] shrink-0 place-items-center rounded-[0.6rem] font-semibold '
  + 'text-[var(--nim-on-primary)] bg-gradient-to-br from-[var(--nim-purple)] to-[var(--nim-primary)]';
export const TEXT_LINK = 'mt-4 inline-block font-medium text-[var(--nim-primary)] no-underline hover:underline';
